import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { ClockIcon } from '@heroicons/react/16/solid';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DurationBar } from '../../components/charts';
import { Button } from '../../components/ui/Button';
import { Chip } from '../../components/ui/Chip';
import { Money } from '../../components/ui/Money';
import { PaymentChip } from '../../components/ui/StatusChip';
import { RowActions } from '../../components/ui/RowActions';
import { ADVANCE_LABEL_KEY } from '../../components/ui/status';
import type { Order, OrderStatus } from '../../types/Order';
import { BOARD_STATUSES, COLLAPSED_LINES, LATE_AFTER_MIN, MAX_LINES, UNSEEN_VISIBLE_MS, WARN_AFTER_MIN } from './config';
import { isTableNumber, PaymentChange } from './orderApi';
import { orderMenu } from './orderMenu';
import { formatDuration, formatElapsed, liveSeconds, minutesSince } from './time';

export interface OrderTicketProps {
    order: Order;
    /** Board clock (ms), ticks every 30 s. */
    now: number;
    unseen: boolean;
    /** Play the enter animation (the ticket arrived or moved after the board was first shown). */
    animateIn: boolean;
    /** Name the order's location (the board shows all locations). */
    showLocation: boolean;
    saving: boolean;
    /** From @hello-pangea/dnd; null while dragging is off. The whole ticket is the handle. */
    dragHandleProps: DraggableProvidedDragHandleProps | null;
    dragging: boolean;
    onSeen: (id: string) => void;
    onAdvance: (order: Order) => void;
    onPay: (order: Order, p: PaymentChange) => void;
    onMove: (order: Order, to: OrderStatus) => void;
    onCancel: (order: Order) => void;
}

/** Decorative: the table or "To go" is always stated in words next to it. */
export const TableBadge = ({ tableNumber }: { tableNumber: string | null }) =>
    tableNumber ? (
        <div
            aria-hidden="true"
            className={clsx(
                'flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-control bg-inverse font-semibold tabular-nums text-on-inverse',
                tableNumber.length <= 2 ? 'text-ticket' : tableNumber.length <= 4 ? 'text-[17px]' : 'px-1 text-center text-[11px] leading-[1.15]'
            )}
        >
            {/* Labels are free text up to 40 characters: long ones wrap inside the square, two lines at most. */}
            <span className="line-clamp-2 break-all">{tableNumber}</span>
        </div>
    ) : (
        <div aria-hidden="true" className="flex size-12 shrink-0 items-center justify-center rounded-control bg-sunken text-muted">
            <ShoppingBagIcon className="size-6" />
        </div>
    );

/** Table label ("Table 4", "Terrace 4") or the To go chip, for the ticket, the table view and the history. */
export const OrderPlace = ({ order }: { order: Pick<Order, 'orderType' | 'tableNumber'> }) => {
    const { t } = useTranslation(['orders', 'common']);
    return order.orderType === 'togo' || !order.tableNumber ? (
        <Chip variant="info" icon={ShoppingBagIcon}>
            {t('common:orderType.togo')}
        </Chip>
    ) : (
        <span>{isTableNumber(order.tableNumber) ? t('ticket.tableNumber', { number: order.tableNumber }) : order.tableNumber}</span>
    );
};

/** One order on the shift board. One primary action per status; everything else lives in the overflow menu. */
export const OrderTicket = ({
    order,
    now,
    unseen,
    animateIn,
    showLocation,
    saving,
    dragHandleProps,
    dragging,
    onSeen,
    onAdvance,
    onPay,
    onMove,
    onCancel,
}: OrderTicketProps) => {
    const { t, i18n } = useTranslation(['orders', 'common']);
    const root = useRef<HTMLElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [animate] = useState(animateIn);
    const headingId = `ticket-${order.id}`;

    // Unseen clears after the ticket has been half visible for UNSEEN_VISIBLE_MS with the tab focused.
    useEffect(() => {
        const el = root.current;
        if (!unseen || !el) return;
        let timer: number | undefined;
        const arm = () => {
            timer = window.setTimeout(() => {
                if (document.hasFocus() && !document.hidden) onSeen(order.id);
                else arm();
            }, UNSEEN_VISIBLE_MS);
        };
        const io = new IntersectionObserver(
            ([entry]) => {
                window.clearTimeout(timer);
                if (entry?.isIntersecting) arm();
            },
            { threshold: 0.5 }
        );
        io.observe(el);
        return () => {
            io.disconnect();
            window.clearTimeout(timer);
        };
    }, [unseen, order.id, onSeen]);

    const minutes = minutesSince(order.createdAt, now);
    const watched = order.status === 'pending' || order.status === 'accepted';
    const late = watched && minutes >= LATE_AFTER_MIN;
    const warn = watched && minutes >= WARN_AFTER_MIN;
    const seconds = liveSeconds(order, now);
    const inState = Math.floor((seconds[order.status as keyof typeof seconds] ?? 0) / 60);
    const approx = order.timing.estimated ? '~' : '';

    const lines = order.orderItems;
    const collapsed = lines.length > MAX_LINES && !expanded;
    const shown = collapsed ? lines.slice(0, COLLAPSED_LINES) : lines;

    const unpaid = order.paymentStatus === 'unpaid';
    const advanceKey = ADVANCE_LABEL_KEY[order.status];
    const actions = orderMenu(t, order, { onPay, onMove, onCancel });

    return (
        <article
            ref={root}
            aria-labelledby={headingId}
            {...dragHandleProps}
            // the handle's own role="button" would nest the ticket's buttons inside a button
            role="group"
            aria-roledescription={dragHandleProps ? t('dnd.roleDescription') : undefined}
            className={clsx(
                'flex flex-col gap-3 rounded-card p-4 shadow-hairline lg:max-xl:p-3',
                unseen ? 'bg-tone-warning-bg ring-2 ring-inset ring-accent' : 'bg-surface',
                animate && 'animate-ticket-in',
                dragHandleProps && 'lg:cursor-grab',
                dragging && 'shadow-e3 lg:cursor-grabbing'
            )}
        >
            <div className="flex items-start gap-3">
                <TableBadge tableNumber={order.orderType === 'togo' ? null : order.tableNumber} />
                <div className="min-w-0 flex-1">
                    <h3 id={headingId} className="break-all text-ticket tabular-nums text-strong lg:max-2xl:text-heading">
                        <Link to={`/orders/${order.id}`} onClick={() => onSeen(order.id)} className="hover:underline">
                            #{order.orderNumber}
                        </Link>
                    </h3>
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-muted">
                        {showLocation && order.locationName && <span className="font-semibold text-strong">{order.locationName}</span>}
                        <OrderPlace order={order} />
                    </p>
                </div>
                <div className="-mr-2 -mt-1">
                    <RowActions actions={actions} label={`#${order.orderNumber}`} />
                </div>
            </div>

            <div>
                <p className="flex flex-wrap items-center gap-x-2 text-small tabular-nums">
                    <time
                        dateTime={order.timing.statusSince}
                        title={new Date(order.timing.statusSince).toLocaleString(i18n.language)}
                        className={clsx(
                            'inline-flex items-center gap-1 whitespace-nowrap',
                            late ? 'font-semibold text-danger' : warn ? 'font-semibold text-tone-warning-fg' : 'text-strong'
                        )}
                    >
                        {late && <ClockIcon className="size-4" aria-hidden="true" />}
                        {t('ticket.inState', { status: t(`common:status.${order.status}`), time: `${approx}${formatElapsed(inState, t)}` })}
                    </time>
                    <time dateTime={order.createdAt} className="whitespace-nowrap text-caption text-muted">
                        {t('ticket.total', { time: `${approx}${formatElapsed(minutes, t)}` })}
                    </time>
                    {order.timing.estimated && <span className="sr-only">{t('ticket.estimated')}</span>}
                </p>
                <DurationBar
                    compact
                    className="mt-1.5"
                    caption={t('ticket.durations', { number: order.orderNumber })}
                    segments={BOARD_STATUSES.map((s) => ({ key: s, label: t(`common:status.${s}`), minutes: seconds[s] / 60 }))}
                    formatValue={(m) => formatDuration(m, t)}
                />
            </div>

            <ul className="flex flex-col gap-1">
                {shown.map((item) => (
                    <li key={item.id} className="flex gap-1 text-heading text-strong">
                        <span className="w-10 shrink-0 tabular-nums">{item.quantity}</span>
                        <span aria-hidden="true">×</span>
                        <span className="min-w-0 break-words pl-1">{item.name}</span>
                    </li>
                ))}
            </ul>
            {lines.length > MAX_LINES && (
                <Button variant="link" className="self-start" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
                    {expanded ? t('ticket.fewer') : t('ticket.more', { count: lines.length - COLLAPSED_LINES })}
                </Button>
            )}

            {order.customerNotes && (
                <div className="rounded-control bg-sunken p-3">
                    <p className="text-caption text-muted">{t('ticket.note')}</p>
                    <p className="whitespace-pre-wrap break-words text-small text-strong">{order.customerNotes}</p>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <Money amount={order.totalAmount} symbol={order.currencySymbol} position={order.currencySymbolPosition} className="text-heading text-strong" />
                <div className="flex items-center gap-1">
                    <PaymentChip status={order.paymentStatus} method={order.paymentMethod} orderStatus={order.status} />
                    {!unpaid && (
                        <Button variant="quiet" size="sm" disabled={saving} onClick={() => onPay(order, { status: 'unpaid' })}>
                            {t('ticket.markUnpaid')}
                        </Button>
                    )}
                </div>
            </div>

            {unpaid && (
                <div className="grid grid-cols-2 gap-2">
                    <span className="col-span-2 text-small text-muted">{t('ticket.markPaid')}</span>
                    <Button variant="outline" disabled={saving} onClick={() => onPay(order, { status: 'paid', method: 'cash' })}>
                        {t('common:payment.cash')}
                    </Button>
                    <Button variant="outline" disabled={saving} onClick={() => onPay(order, { status: 'paid', method: 'card' })}>
                        {t('common:payment.card')}
                    </Button>
                </div>
            )}

            {advanceKey && (
                <Button size="xl" fullWidth variant={order.status === 'pending' ? 'primary' : 'strong'} disabled={saving} onClick={() => onAdvance(order)}>
                    {t(advanceKey, { ns: 'common' })}
                </Button>
            )}
        </article>
    );
};
